import { memoize } from "lodash";

// in specific file to avoid circular dependency
export const isDocustream = memoize(
  () =>
    Boolean(
      /https?:\/\/app.(.*)docustream.ai/.exec(globalThis.location.href)
    ) ||
    globalThis.location.search.indexOf("__docustream") >= 0 ||
    // docustream.local for local development
    Boolean(/https?:\/\/docustream.localhost/.exec(globalThis.location.href))
);

export const isLocal = memoize(() =>
  window.location.hostname.includes("localhost")
);

export const getLanguageVariant = (lang: string): string => {
  if (lang.includes("-")) {
    return lang;
  }
  const langMap: Record<string, string> = {
    en: "en-US",
    fr: "fr-FR",
    es: "es-ES",
  };
  return langMap[lang] || lang;
};

// Inverse of getLanguageVariant: "en-US" -> "en". The backend stores the short
// code while the UI works with full locales.
export const getLanguageCode = (locale: string): string =>
  locale.split("-")[0] || locale;

// The project Wizard lists en-US first; every language selector follows it.
export const withEnglishFirst = <T extends { value: string }>(
  options: T[]
): T[] => [
  ...options.filter((option) => option.value === "en-US"),
  ...options.filter((option) => option.value !== "en-US"),
];

// Keeps a saved value the backend no longer lists (or not loaded yet) readable.
export const withUnlistedLanguage = (
  options: { value: string; label: string }[],
  value: unknown,
  locale: string
): { value: string; label: string }[] =>
  typeof value === "string" &&
  value !== "" &&
  value !== "auto" &&
  !options.some((option) => option.value === value)
    ? [...options, { value, label: getLanguageName(value, locale, true) }]
    : options;

const getCountryName = (countryCode: string, language: string) => {
  if (!countryCode) return "";
  const regionNames = new Intl.DisplayNames(language, { type: "region" });
  const regionName = regionNames.of(countryCode);
  return regionName;
};
export const getLanguageName = (
  locale: string,
  language: string,
  withCustomCountry = false
) => {
  try {
    const [lang = "", country] = locale.split("-");
    // There are situations where the storyboard has no language which causes a runtime error
    // if (!lang) return "en-US";
    const languageNames = new Intl.DisplayNames(
      country ? lang : getLanguageVariant(lang),
      { type: "language" }
    );
    const languageName = languageNames.of(lang);
    if (
      withCustomCountry &&
      country &&
      lang.toLocaleLowerCase() !== country.toLocaleLowerCase()
    ) {
      return `${languageName} (${getCountryName(country, language)})`;
    }
    if (!languageName) return "N/A";
    return languageName.slice(0, 1).toUpperCase() + languageName.slice(1);
  } catch {
    return "N/A";
  }
};
